import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";

interface ActivityItem {
  id: string;
  room_id: string;
  user_id: string;
  action: string;
  content_title: string;
  content_type: string | null;
  created_at: string;
  user_name?: string;
}

export const RoomActivityFeed: React.FC<{ roomId: string }> = ({ roomId }) => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("room_activity")
        .select("*")
        .eq("room_id", roomId)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error || !data || data.length === 0) {
        setActivities([]);
        setLoading(false);
        return;
      }

      const userIds = [...new Set(data.map((a) => a.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, name, email")
        .in("id", userIds);

      const profileMap = new Map(
        (profiles || []).map((p) => [p.id, p.name || p.email?.split("@")[0] || "User"])
      );

      setActivities(
        data.map((a) => ({ ...a, user_name: profileMap.get(a.user_id) || "User" }))
      );
      setLoading(false);
    };

    fetch();
  }, [roomId]);

  if (loading || activities.length === 0) return null;

  const displayed = collapsed ? activities.slice(0, 3) : activities;
  const remaining = activities.length - 3;

  return (
    <div className="mb-4 rounded-xl border border-border/40 bg-card p-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/50">
          Activity
        </span>
        {!collapsed && (
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="text-[10px] font-mono text-muted-foreground/30 hover:text-muted-foreground/60 transition-colors"
          >
            collapse
          </button>
        )}
      </div>

      {/* Rows */}
      <div className="space-y-0.5">
        {displayed.map((a) => {
          const isAdd = a.action === "added";
          const timeAgo = formatDistanceToNow(new Date(a.created_at))
            .replace(" minutes", "m")
            .replace(" minute", "m")
            .replace(" hours", "h")
            .replace(" hour", "h")
            .replace(" days", "d")
            .replace(" day", "d")
            .replace("about ", "")
            .replace("less than am", "<1m");

          return (
            <div
              key={a.id}
              className="grid grid-cols-[12px_1fr_auto] gap-2 items-center rounded-md px-2 py-1.5 hover:bg-muted/30 transition-colors"
            >
              {/* Dot */}
              <div className="flex items-center justify-center">
                <div
                  className={`w-1.5 h-1.5 rounded-full ${
                    isAdd ? "bg-emerald-500" : "bg-muted-foreground/30"
                  }`}
                />
              </div>

              {/* Body */}
              <div className="text-[11px] truncate leading-tight">
                <span className="font-medium text-foreground">
                  {a.user_name}
                </span>
                <span className="text-muted-foreground/60 mx-1">
                  {a.action}
                </span>
                <span className="text-foreground/80">
                  {a.content_title}
                </span>
              </div>

              {/* Time */}
              <span className="text-[10px] font-mono text-muted-foreground/40 whitespace-nowrap">
                {timeAgo}
              </span>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {collapsed && remaining > 0 && (
        <div className="mt-1.5 flex justify-center border-t border-border/30 pt-1.5">
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="text-[10px] font-mono text-muted-foreground/25 hover:text-muted-foreground/50 transition-colors"
          >
            +{remaining} more
          </button>
        </div>
      )}
    </div>
  );
};
